import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnDestroy,
} from '@angular/core';
import { assert, isDefined } from '@sama/js-core';
import { DirectiveSuperclass } from '@sama/ng-core';
import { filter, skip } from 'rxjs';
import {
  highlightFeedbackErrorTypes,
  isHighlightFeedbackErrorType,
} from '../../../../team-apis/otterdam/feedback';
import { FeedbackPanelComponent } from '../../../feedback/views/feedback-panel/feedback-panel.component';
import {
  Feedback,
  UpsertFeedbackComponent,
} from '../../../feedback/views/upsert-feedback/upsert-feedback.component';
import { TaskWorkspaceStore } from '../../../h3ml/state/task-workspace-store';
import { HighlightFeedbackPanelService } from '../../services/highlight-feedback-panel.service';
import { HighlightService } from '../../services/highlight.service';

@Component({
  selector: 'h3-add-highlight-feedback-panel',
  templateUrl: './add-highlight-feedback-panel.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FeedbackPanelComponent, UpsertFeedbackComponent],
  host: {
    class: 'mat-elevation-z4 popover-panel',
  },
})
export class AddHighlightFeedbackPanelComponent
  extends DirectiveSuperclass
  implements OnDestroy
{
  private readonly store = inject(TaskWorkspaceStore);
  private readonly highlightService = inject(HighlightService);
  private readonly feedbackPanelService = inject(HighlightFeedbackPanelService);

  possibleErrorTags = [...highlightFeedbackErrorTypes];

  private currentHighlightChanged = false;

  constructor() {
    super();

    const currentHighlightChanged$ = this.store('highlightState')(
      'inProgress',
    ).$.pipe(skip(1), filter(isDefined));

    // We want to close the panel when selecting new text, but keep the new
    // selection.
    this.subscribeTo(currentHighlightChanged$, () => {
      this.currentHighlightChanged = true;
      this.closePanel();
    });
  }

  ngOnDestroy() {
    // Since the panel is managed externally, it can be closed without the close
    // button being clicked. In those cases, we want to clear the selection if
    // it hasn't changed.
    if (!this.currentHighlightChanged) {
      this.highlightService.clearInProgress();
    }
  }

  getTitle() {
    const { inProgress } = this.store.state().highlightState;
    assert(inProgress, 'In progress highlight must be set before saving');

    return `Add Highlight #${inProgress.objectId}`;
  }

  saveHighlight({ errorTag, comment }: Feedback) {
    assert(isHighlightFeedbackErrorType(errorTag), 'invalid errorTag');
    assert(errorTag, 'errorTag is required');
    this.highlightService.saveInProgressHighlight({
      errorTag,
      comment: comment || undefined,
    });
    this.feedbackPanelService.showHighlightList('reopen');
  }

  close() {
    this.closePanel();
    this.highlightService.clearInProgress();
  }

  private closePanel() {
    this.unsubscribe();
    this.feedbackPanelService.close();
  }
}
